import { HUD, SPACING, UI_LOGICAL_SIZE } from "odyssey-shared";
import { SECTION_ORDER, sectionSettings, type SettingId, type SettingsSection } from "./settings-model.ts";

/**
 * Which sections and rows Settings shows, and where its window sits (P1.9-T44; ADR-061's amendment "Settings in
 * sections, and nothing applies until Apply", decisions 5 and 8). A section shows only when it holds an entry
 * built today: Controls holds each action's key (P1.9-T50), Social holds Party invitations on a page
 * that can change it (an account session), and Account holds Log out everywhere for an account session alone.
 * The window is the design's 612 by 236 at 14, 44 on the 640 by 360 grid and never passes the page.
 */

/**
 * The window as the design draws it (`ui_kits/surfaces/SettingsSurfaces.jsx`), in logical pixels: its size and
 * its top on the 640 by 360 grid, the section list's width, the gap each side of the rule between the list and
 * the pane, the rule's width, a row's least height, the gap between rows, the changed mark's side and its gap to
 * the label, the gap between a row's label and its control, the pane's gap to its scrollbar, and the gap between
 * the footer's count, Cancel and Apply (3, where the kit's window footer keeps 4).
 */
export const SETTINGS_WINDOW = { width: 612, height: 236, top: 44, sections: 96, gap: SPACING[4], rule: 1, row: 16, rowGap: SPACING[1], mark: 2, markGap: SPACING[1], controlGap: SPACING[3], scrollGap: SPACING[1], footerGap: SPACING[2] } as const;

/**
 * The sections that hold an entry whatever the session: Display, Interface, Audio, Controls and Accessibility.
 */
const ALWAYS: readonly SettingsSection[] = ["display", "interface", "audio", "controls", "access"];

/**
 * The sections the section list shows, in the design's order.
 *
 * @param page Whether the page plays an account session, which alone has Log out everywhere; whether the page can
 *   change Party invitations (`social`, false unless given), without which Social shows nothing; and whether Controls
 *   shows (`controls`, true unless given): a touch page with no keyboard in use leaves it out, by the author's ruling
 *   of 2026-10-07, "hidden from mobile, unless a bluetooth/keyboard is plugged in."
 * @returns The sections that hold an entry.
 * @example
 * shownSections({ account: false });
 * // ["display", "interface", "audio", "controls", "access"]
 */
export function shownSections(page: { readonly account: boolean; readonly controls?: boolean; readonly social?: boolean }): readonly SettingsSection[] {
  return SECTION_ORDER.filter((section) => (ALWAYS.includes(section) && (section !== "controls" || page.controls !== false)) || (section === "account" && page.account) || (section === "social" && page.social === true));
}

/**
 * The rows of a section's pane that are settings, in order.
 *
 * @param section The section.
 * @param page Whether the page has the performance panel, without which Display offers no Show performance,
 *   whether it has a render resolution, without which Display offers no Render resolution, and whether it has a
 *   frame limit, without which Display offers no Frame limit, and whether a keyboard is in use (`keys`, true unless
 *   given), without which Interface offers no Show key numbers on the bar. Controls lists every
 *   action whatever the page has, since the table holds every key and a clash must never name a row not shown.
 * @returns The settings' ids.
 */
export function shownRows(section: SettingsSection, page: { readonly performance: boolean; readonly render: boolean; readonly frameLimit: boolean; readonly keys?: boolean }): readonly SettingId[] {
  return sectionSettings(section).filter((id) => (id !== "barKeys" || page.keys !== false) && (id !== "performance" || page.performance) && (id !== "renderDivisor" || page.render) && (id !== "interfaceStep" || page.render) && (id !== "frameLimit" || page.frameLimit));
}

/**
 * Where the window sits in the page's logical area and how large it is: the design's size, or the area less the
 * HUD's margin on each side where that is smaller; centred across; and down by the design's share of the free
 * height (44 of the 124 free on the 640 by 360 grid), never nearer the top than the margin.
 *
 * @param area The area's size in logical pixels, as measured; a fraction of a pixel is dropped.
 * @returns The window's box in whole logical pixels.
 * @example
 * settingsWindowBox({ width: 445, height: 230 });
 * // { left: 4, top: 4, width: 437, height: 222 }
 */
export function settingsWindowBox(area: { readonly width: number; readonly height: number }): { readonly left: number; readonly top: number; readonly width: number; readonly height: number } {
  const across = Math.floor(area.width);
  const down = Math.floor(area.height);
  const width = Math.max(0, Math.min(SETTINGS_WINDOW.width, across - 2 * HUD.margin));
  const height = Math.max(0, Math.min(SETTINGS_WINDOW.height, down - 2 * HUD.margin));
  const free = UI_LOGICAL_SIZE.height - SETTINGS_WINDOW.height;
  return {
    left: Math.max(HUD.margin, Math.floor((across - width) / 2)),
    top: Math.max(HUD.margin, Math.floor(((down - height) * SETTINGS_WINDOW.top) / free)),
    width,
    height,
  };
}
